import { PageId } from 'constants/pages'
import { isEmpty } from 'lodash'
import { TStep } from 'nove8/types'
import { StatesConfig } from 'xstate'
import { useZustandGlobalLocalStore } from 'zustandGlobalLocalStore'
import { useZustandSessionStore } from 'zustandGlobalSessionStore'
import { useZustandGlobalStore, useZustandStore } from 'zustandGlobalStore'
import { getCurrentDogProfile } from './helpers/getCurrentDogProfile'
import { SIX_MONTHS_OR_YOUNGER } from './pages/questions/constants'

const getHasAnswers = () =>
  !isEmpty(
    useZustandSessionStore.getState().userAnswers ||
      useZustandGlobalStore.getState().lead?.quizzes ||
      null,
  )

const shouldRedirectUser = () => {
  const userSubscriptions = useZustandGlobalStore.getState().lead?.subscriptions
  const hasRedirectFlow = userSubscriptions?.find(
    (subscription) =>
      subscription.productName === 'vc' &&
      !['created', 'cancelled'].includes(subscription.status),
  )
  return hasRedirectFlow
}

const getHasAgeFromProfile = () =>
  !!getCurrentDogProfile()?.data?.age_group_name &&
  getCurrentDogProfile()?.data?.age_group_name !== SIX_MONTHS_OR_YOUNGER

const getHasBreedFromProfile = () =>
  !!getCurrentDogProfile()?.data?.selected_breed_name

export const getXStateConfigWoofzCoaching = (
  pages: TStep[],
): StatesConfig<any, any, any> => ({
  initial: {
    // Pay attention to the order of the conditions!
    always: [
      // Cover case when page was reloaded or connection was lost
      // during the purchase polling
      {
        cond: (): boolean => {
          const userHasChat = useZustandStore.getState().userHasChat
          return !!shouldRedirectUser() && userHasChat
        },
        target: PageId.ALL_SET_COACHING,
      },
      {
        cond: (): boolean => !!useZustandGlobalLocalStore.getState().purchaseId,
        target:
          useZustandGlobalLocalStore.getState().purchasePageId ||
          pages[0].pageId,
      },
      {
        cond: (): boolean => {
          const hasAnswers = getHasAnswers()
          return hasAnswers
        },
        target:
          pages.find((dynamicPage) => {
            const pageIdFromUrl =
              window.location.pathname.split('/').pop() || ''
            return pageIdFromUrl === dynamicPage.pageId
          })?.pageId || pages[0].pageId,
      },
      {
        target: pages[0].pageId, // Always transition to first page as fallback
      },
    ],
  },
  ...pages.reduce(
    (acc, page, index) => ({
      ...acc,
      [page.pageId]: {
        on: {
          NEXT_FLOW_STEP: {
            target: pages[index + 1]?.pageId,
          },
          PREVIOUS_FLOW_STEP: {
            target:
              index - 1 >= 0 ? pages[index - 1].pageId : pages[index].pageId,
          },
          SKIP_ONE_STEP_BACK: {
            target:
              index - 2 >= 0 ? pages[index - 2].pageId : pages[index].pageId,
          },
          ...(page.pageId === PageId.PROBLEM_DESCRIPTION && {
            NEXT_FLOW_STEP: [
              {
                cond: () =>
                  !!getHasBreedFromProfile() && !!getHasAgeFromProfile(),
                target: PageId.DOG_PROFILE_COACHING,
              },
              {
                cond: () =>
                  !!getHasBreedFromProfile() && !getHasAgeFromProfile(),
                target: PageId.DOG_AGE_COACHING,
              },
              {
                target: PageId.DOG_BREED_COACHING,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_BREED_COACHING && {
            NEXT_FLOW_STEP: [
              {
                cond: () => !getHasAgeFromProfile(),
                target: PageId.DOG_AGE_COACHING,
              },
              {
                target: PageId.DOG_PROFILE_COACHING,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_AGE_COACHING && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: () => !!getHasBreedFromProfile(),
                target: PageId.PROBLEM_DESCRIPTION,
              },
              {
                target: PageId.DOG_BREED_COACHING,
              },
            ],
          }),
          ...(page.pageId === PageId.DOG_PROFILE_COACHING && {
            PREVIOUS_FLOW_STEP: [
              {
                cond: () =>
                  !!getHasBreedFromProfile() &&
                  !!getCurrentDogProfile()?.data?.age_group_name,
                target: PageId.PROBLEM_DESCRIPTION,
              },
              {
                cond: () =>
                  !getCurrentDogProfile()?.data?.age_group_name ||
                  getCurrentDogProfile()?.data?.age_group_name ===
                    SIX_MONTHS_OR_YOUNGER,
                target: PageId.DOG_AGE_COACHING,
              },
              {
                target: PageId.DOG_BREED_COACHING,
              },
            ],
          }),
          ...(page.pageId === PageId.CANCEL_OFFER_COACHING && {
            NEXT_FLOW_STEP: {
              target: PageId.CALENDLY_COACHING,
            },
          }),
          ...(page.pageId === PageId.SUBSCRIPTIONS_COACHING && {
            NEXT_FLOW_STEP: {
              target: PageId.CALENDLY_COACHING,
            },
            PREVIOUS_FLOW_STEP: {
              target: PageId.CANCEL_OFFER_COACHING,
            },
          }),
        },
      },
    }),
    {},
  ),
  calendly_coaching: {
    on: {
      NEXT_FLOW_STEP: 'all_set_coaching',
    },
  },
  all_set_coaching: {
    type: 'final',
  },
})
